import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, CheckCircle, AlertCircle, Loader2, ArrowRight, Clock, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

// Timeout after 120 seconds of pending state
const PENDING_TIMEOUT_MS = 120000;
const RETRY_INTERVAL_MS = 3000;
const MAX_RETRIES = 40; // 40 retries * 3s = 120s

const WeddingInsightThankYou = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const purchaseId = searchParams.get('purchase_id');
  
  const [verificationStatus, setVerificationStatus] = useState('loading'); // loading, verified, pending, timeout, error
  const [purchaseData, setPurchaseData] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [copied, setCopied] = useState(false);
  const startTimeRef = useRef(Date.now());

  // Track elapsed time for pending state
  useEffect(() => {
    if (verificationStatus === 'pending') {
      const timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsedSeconds(elapsed);
        
        // Timeout after 120 seconds
        if (elapsed >= 120) {
          setVerificationStatus('timeout');
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [verificationStatus]);

  useEffect(() => {
    if (!purchaseId) {
      setVerificationStatus('error');
      return;
    }

    const verifyPurchase = async () => {
      try {
        const response = await axios.get(`${API}/wedding-insight/verify-purchase/${purchaseId}`);
        const data = response.data;
        
        // Always store purchase data for reference
        setPurchaseData(data);
        
        if (data.verified) {
          setVerificationStatus('verified');
        } else if (data.status === 'pending' && retryCount < MAX_RETRIES) {
          // Payment webhook may not have arrived yet - retry
          setVerificationStatus('pending');
          setTimeout(() => setRetryCount(prev => prev + 1), RETRY_INTERVAL_MS);
        } else if (data.status === 'pending') {
          // Max retries reached
          setVerificationStatus('timeout');
        } else {
          setVerificationStatus('pending');
        }
      } catch (error) {
        console.error('Verification error:', error);
        if (retryCount < 5) {
          setTimeout(() => setRetryCount(prev => prev + 1), RETRY_INTERVAL_MS);
        } else {
          setVerificationStatus('error');
        }
      }
    };

    verifyPurchase();
  }, [purchaseId, retryCount]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  // Reference ID display component - shows purchase_id and payment_intent
  const ReferenceInfo = ({ variant = 'light' }) => {
    const textClass = variant === 'dark' ? 'text-white/50' : 'text-[#8A8580]';
    const labelClass = variant === 'dark' ? 'text-white/40' : 'text-[#A8A29E]';
    const bgClass = variant === 'dark' ? 'bg-white/5' : 'bg-[#FAF9F7]';
    
    return (
      <div className={`${bgClass} p-4 mt-6 text-left`}>
        <p className={`text-xs ${labelClass} mb-3 uppercase tracking-wide`}>Reference Information</p>
        <div className="space-y-2 text-sm font-mono">
          <div className="flex items-center justify-between gap-2">
            <span className={labelClass}>Purchase ID:</span>
            <div className="flex items-center gap-2">
              <span className={textClass}>{purchaseId}</span>
              <button 
                onClick={() => copyToClipboard(purchaseId)} 
                className={`${textClass} hover:opacity-80`}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
          {purchaseData?.payment_intent && (
            <div className="flex items-center justify-between gap-2">
              <span className={labelClass}>Payment Intent:</span>
              <div className="flex items-center gap-2">
                <span className={textClass}>{purchaseData.payment_intent}</span>
                <button 
                  onClick={() => copyToClipboard(purchaseData.payment_intent)} 
                  className={`${textClass} hover:opacity-80`}
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>
          )}
          {purchaseData?.stripe_session_id && (
            <div className="flex items-center justify-between gap-2">
              <span className={labelClass}>Session ID:</span>
              <span className={`${textClass} text-xs truncate max-w-[200px]`}>{purchaseData.stripe_session_id}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Loading state
  if (verificationStatus === 'loading') {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center px-6">
        <div className="text-center">
          <Loader2 size={48} className="text-[#881337] animate-spin mx-auto mb-6" />
          <p className="text-[#5C5652] text-lg">Verifying your purchase...</p>
        </div>
      </div>
    );
  }

  // Timeout state - after 120 seconds
  if (verificationStatus === 'timeout') {
    return (
      <div className="min-h-screen bg-[#FAF9F7]">
        <nav className="px-6 md:px-12 lg:px-20 py-6">
          <div className="max-w-6xl mx-auto">
            <div 
              className="flex items-center gap-2.5 cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <Heart className="text-[#881337]" size={22} fill="#881337" />
              <span className="text-lg font-serif text-[#2C2825]">VowFlow</span>
            </div>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <Clock size={40} className="text-yellow-600" />
          </div>
          
          <h1 
            className="font-serif text-3xl md:text-4xl text-[#2C2825] mb-6"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Payment Processing Taking Longer Than Expected
          </h1>
          
          <p className="text-lg text-[#5C5652] mb-6">
            {"Don't worry — your payment is likely being processed by Stripe. This can occasionally take a few extra minutes."}
          </p>

          <div className="bg-white border border-[#E8E5E1] p-6 mb-8 text-left">
            <h3 className="font-medium text-[#2C2825] mb-4">What to do next:</h3>
            <ol className="space-y-3 text-[#5C5652]">
              <li className="flex gap-3">
                <span className="font-medium text-[#881337]">1.</span>
                <span>Check your email for a confirmation from Stripe or VowFlow</span>
              </li>
              <li className="flex gap-3">
                <span className="font-medium text-[#881337]">2.</span>
                <span>Check your bank statement to confirm the charge went through</span>
              </li>
              <li className="flex gap-3">
                <span className="font-medium text-[#881337]">3.</span>
                <span>If you see a charge, your intake form link will arrive shortly via email</span>
              </li>
              <li className="flex gap-3">
                <span className="font-medium text-[#881337]">4.</span>
                <span>Contact support if you have questions (include the reference info below)</span>
              </li>
            </ol>
          </div>

          <div className="bg-[#2C2825] p-6 mb-8">
            <p className="text-white/80 text-sm mb-4">
              Keep this information handy for support inquiries:
            </p>
            <ReferenceInfo variant="dark" />
          </div>

          <p className="text-sm text-[#8A8580]">
            Contact: support@vowflow.app
          </p>
        </div>
      </div>
    );
  }

  // Pending state (waiting for webhook)
  if (verificationStatus === 'pending') {
    return (
      <div className="min-h-screen bg-[#FAF9F7]">
        <nav className="px-6 md:px-12 lg:px-20 py-6">
          <div className="max-w-6xl mx-auto">
            <div 
              className="flex items-center gap-2.5 cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <Heart className="text-[#881337]" size={22} fill="#881337" />
              <span className="text-lg font-serif text-[#2C2825]">VowFlow</span>
            </div>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 bg-[#881337]/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <Loader2 size={40} className="text-[#881337] animate-spin" />
          </div>
          
          <h1 
            className="font-serif text-3xl md:text-4xl text-[#2C2825] mb-6"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Confirming Your Payment
          </h1>
          
          <p className="text-lg text-[#5C5652] mb-4">
            {"We're verifying your payment with Stripe. This usually takes just a few seconds."}
          </p>
          
          <p className="text-sm text-[#8A8580] mb-8">
            Checking... {elapsedSeconds}s
          </p>

          {/* Progress bar */}
          <div className="w-full max-w-xs mx-auto h-1 bg-[#E8E5E1] rounded-full overflow-hidden mb-8">
            <div 
              className="h-full bg-[#881337] transition-all duration-1000"
              style={{ width: `${Math.min((elapsedSeconds / 120) * 100, 100)}%` }}
            />
          </div>
          
          <div className="bg-white border border-[#E8E5E1] p-6 text-sm text-[#5C5652]">
            <p>
              {"If this page doesn't update within 2 minutes, check your email for confirmation or contact support."}
            </p>
          </div>

          <ReferenceInfo variant="light" />
        </div>
      </div>
    );
  }

  // Error state
  if (verificationStatus === 'error' || !purchaseId) {
    return (
      <div className="min-h-screen bg-[#FAF9F7]">
        <nav className="px-6 md:px-12 lg:px-20 py-6">
          <div className="max-w-6xl mx-auto">
            <div 
              className="flex items-center gap-2.5 cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <Heart className="text-[#881337]" size={22} fill="#881337" />
              <span className="text-lg font-serif text-[#2C2825]">VowFlow</span>
            </div>
          </div>
        </nav>

        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <AlertCircle size={40} className="text-red-500" />
          </div>
          
          <h1 
            className="font-serif text-3xl md:text-4xl text-[#2C2825] mb-6"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Unable to Verify Purchase
          </h1>
          
          <p className="text-lg text-[#5C5652] mb-8">
            {"We couldn't verify your purchase. This may be because the payment is still processing or the purchase link is invalid."}
          </p>
          
          <div className="space-y-4">
            <button
              onClick={() => navigate('/wedding-insight')}
              className="px-8 py-4 bg-[#881337] text-white font-medium hover:bg-[#6F0F2D] transition-colors"
            >
              Return to Wedding Insight
            </button>
            
            <p className="text-sm text-[#8A8580]">
              If you believe this is an error, please contact support@vowflow.app
            </p>
          </div>

          {purchaseId && <ReferenceInfo variant="light" />}
        </div>
      </div>
    );
  }

  // Verified/Success state
  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <nav className="px-6 md:px-12 lg:px-20 py-6">
        <div className="max-w-6xl mx-auto">
          <div 
            className="flex items-center gap-2.5 cursor-pointer" 
            onClick={() => navigate('/')}
          >
            <Heart className="text-[#881337]" size={22} fill="#881337" />
            <span className="text-lg font-serif text-[#2C2825]">VowFlow</span>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        {/* Success Icon */}
        <div className="w-24 h-24 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-8">
          <CheckCircle size={48} className="text-green-500" />
        </div>
        
        {/* Heading */}
        <h1 
          className="font-serif text-3xl md:text-4xl text-[#2C2825] mb-4"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Payment Confirmed
        </h1>
        
        <p className="text-lg text-[#5C5652] mb-8">
          Thank you{purchaseData?.name ? `, ${purchaseData.name.split(' ')[0]}` : ''}! Your Wedding Insight purchase is complete.
        </p>

        {/* Purchase Details */}
        <div className="bg-white border border-[#E8E5E1] p-8 mb-8 text-left">
          <h2 className="font-medium text-[#2C2825] mb-4">Purchase Details</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-[#5C5652]">Amount Paid</span>
              <span className="text-[#2C2825] font-medium">${purchaseData?.amount_paid || '99.00'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#5C5652]">Email</span>
              <span className="text-[#2C2825]">{purchaseData?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#5C5652]">Payment Status</span>
              <span className="text-green-600 font-medium">Confirmed</span>
            </div>
            {purchaseData?.paid_at && (
              <div className="flex justify-between">
                <span className="text-[#5C5652]">Date</span>
                <span className="text-[#2C2825]">{new Date(purchaseData.paid_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>
          
          {/* Payment Intent - always show for cross-checking */}
          {purchaseData?.payment_intent && (
            <div className="mt-4 pt-4 border-t border-[#E8E5E1]">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#8A8580]">Transaction ID (PaymentIntent)</span>
                <div className="flex items-center gap-2">
                  <code className="text-[#5C5652] text-xs bg-[#FAF9F7] px-2 py-1">{purchaseData.payment_intent}</code>
                  <button 
                    onClick={() => copyToClipboard(purchaseData.payment_intent)} 
                    className="text-[#8A8580] hover:text-[#5C5652]"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Next Steps */}
        <div className="bg-[#2C2825] p-8 mb-8 text-left">
          <h2 className="font-medium text-white mb-4">What Happens Next</h2>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-[#881337] text-white text-sm flex items-center justify-center flex-shrink-0">1</div>
              <div>
                <p className="text-white font-medium">Check Your Email</p>
                <p className="text-white/60 text-sm">{"We've sent a confirmation email with your intake form link."}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-[#881337] text-white text-sm flex items-center justify-center flex-shrink-0">2</div>
              <div>
                <p className="text-white font-medium">Complete the Intake Form</p>
                <p className="text-white/60 text-sm">Share details about your wedding timeline, vendors, and priorities.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-[#881337] text-white text-sm flex items-center justify-center flex-shrink-0">3</div>
              <div>
                <p className="text-white font-medium">Receive Your Insight</p>
                <p className="text-white/60 text-sm">{"We'll analyze your responses and deliver your personalized Wedding Insight."}</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        {!purchaseData?.intake_submitted ? (
          <button
            onClick={() => navigate(`/wedding-insight/intake?purchase_id=${purchaseId}`)}
            className="px-8 py-4 bg-[#881337] text-white font-medium hover:bg-[#6F0F2D] transition-colors flex items-center gap-2 mx-auto"
          >
            Complete Your Intake Form
            <ArrowRight size={18} />
          </button>
        ) : (
          <div className="bg-green-500/10 border border-green-500/20 p-4">
            <p className="text-green-700 font-medium">Intake form already submitted!</p>
            <p className="text-green-600 text-sm mt-1">{"We're reviewing your information and will send your Insight soon."}</p>
          </div>
        )}

        {/* Reference - Purchase ID */}
        <p className="text-xs text-[#A8A29E] mt-8">
          Purchase ID: {purchaseId}
        </p>
      </div>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-[#E8E5E1]">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-[#8A8580]">
            Questions? Contact us at support@vowflow.app
          </p>
        </div>
      </footer>
    </div>
  );
};

export default WeddingInsightThankYou;
